import { Component, OnInit, ViewChild, Input, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { TagsApiService } from '../apis/tags-api.service';
import { DottedDropdownAction } from '../dotted-dropdown/dotted-dropdown.component';
import { Common } from '../lib/common-methods';
import { LocalStorageService } from '../lib/local-storage.service';
import { ModalDialogComponent } from '../modal-dialog/modal-dialog.component';
import { Reservation } from '../model/reservation';
import { ReservationTag, ReservationTagCategory } from '../model/reservation-tag';
import { TagType } from '../model/tag-type';
import { BatchCategoryUpdate, CategoryTagDTO, createTagUpdateDTO, TagDTO } from '../model/tags.dto';
import { ReservationTagModalComponent } from '../reservation-tag-modal/reservation-tag-modal.component';
import * as AppActions from '../store/actions/app.actions';
import * as AppSelectors from '../store/selectors/app.selectors';

@Component({
  selector: 'app-tag-base',
  templateUrl: './tag-base.component.html',
  styleUrls: ['./tag-base.component.scss']
})
export class TagBaseComponent implements OnInit, OnDestroy {

  public categories: ReservationTagCategory[] = [];

  public categoryActions: DottedDropdownAction[] = [];
  public tagActions: DottedDropdownAction[] = [];

  public titleResource = 'menu.reservationTagsMi';

  tags$: Observable<ReservationTagCategory[] | null> = this.store.pipe(select(AppSelectors.selectTags));
  destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() tagType: TagType = "RESERVATION";

  @ViewChild('categoryModal') categoryModal!: ModalDialogComponent;
  @ViewChild('deleteCategoryModal') deleteCategoryModal!: ModalDialogComponent;
  @ViewChild('tagModal') tagModal!: ReservationTagModalComponent;
  @ViewChild('deleteTagModal') deleteTagModal!: ModalDialogComponent;

  constructor(private translate: TranslateService,
    private tagsApi: TagsApiService,
    private localStorageService: LocalStorageService,
    private store: Store<{ reservations: Reservation[] }>) { }

  ngOnInit(): void {
    this.loadActions();
    this.isCategoryValid = false;
    this.titleResource = this.tagType == 'CUSTOMER' ? 'menu.customerTypesMi' : 'menu.reservationTagsMi';

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.categories = tags?.filter(t => t.type == this.tagType)?.map(c => new ReservationTagCategory(c)) ?? [];
    });

    this.store.dispatch({ type: AppActions.loadTags.type });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  loadActions = async () => {
    const justToLoadTranslations = await firstValueFrom(this.translate.get('report.status.completed'));
    this.categoryActions = [
      { name: this.translate.instant('common.buttons.edit'), color: 'default', action: (context: ReservationTagCategory) => this.editCategory(context) },
      { name: this.translate.instant('common.buttons.delete'), color: 'danger', action: (context: ReservationTagCategory) => this.showDeleteCategoryModal(context) }
    ];

    this.tagActions = [
      { name: this.translate.instant('common.buttons.edit'), color: 'default', action: (context: any[]) => this.editTag(context[0], context[1]) },
      { name: this.translate.instant('common.buttons.delete'), color: 'danger', action: (context: any[]) => this.showDeleteTagModal(context[0], context[1]) }
    ];
  }

  searchCategory: string = "";
  searchTag: string = "";
  selectedCategory: ReservationTagCategory = new ReservationTagCategory();
  selectedTag: ReservationTag = new ReservationTag();
  isCategoryValid: boolean = false;
  categoryErrorMessage: string = "";

  private categoryStartOrder: number = 0;
  private categoryStartName: string = "";
  private categoryStartColor: string = "";
  private tagStartOrder: number = 0;
  private tagStartName: string = "";

  clearSearch() {
    this.searchCategory = "";
  }

  clearSearchTag() {
    this.searchTag = "";
  }

  categoryColorChanged(newColor: string) {
    this.selectedCategory.color = newColor;
    this.updateCategoryValid();
  }

  updateCategoryValid() {
    this.categoryErrorMessage = "";
    this.selectedCategory.name = this.selectedCategory?.name.trim();

    if (Common.isNullOrEmpty(this.selectedCategory?.name) || Common.isNullOrEmpty(this.selectedCategory?.color)) {
      this.categoryErrorMessage = this.translate.instant('common.requiredFields');
    } else if (this.categories.find(c => c.id != this.selectedCategory.id && c.name == this.selectedCategory.name) != null) {
      this.categoryErrorMessage = this.translate.instant('common.nameIsUsed');
    }

    this.isCategoryValid = Common.isNullOrEmpty(this.categoryErrorMessage);
  }

  showAddCategoryModal() {
    this.selectedCategory = new ReservationTagCategory();
    this.selectedCategory.order = undefined;
    //this.selectedCategory.order = (this.categories.map(c => c.order ?? 0).sort((a, b) => b - a)[0] ?? 0) + 1;
    this.updateCategoryValid();
    this.categoryStartOrder = 0;
    this.categoryModal.show();
  }

  editCategory(item: ReservationTagCategory) {
    this.selectedCategory = new ReservationTagCategory(item);
    this.updateCategoryValid();
    this.categoryStartOrder = item.order ?? 0;
    this.categoryStartName = item.name;
    this.categoryStartColor = item.color;
    this.categoryModal.show();
  }

  async categoryModalClosed(ok: boolean) {
    if (ok == false) {
      return;
    }

    const batchUpdate = this.createUpdateDTO();
    if (batchUpdate == null) {
      return;
    }

    await firstValueFrom(this.tagsApi.saveTags(batchUpdate));
    this.store.dispatch({ type: AppActions.loadTags.type });
  }

  showDeleteCategoryModal(item: ReservationTagCategory) {
    this.selectedCategory = new ReservationTagCategory(item);
    this.deleteCategoryModal.show();
  }

  async deleteCategoryModalClosed(ok: boolean) {
    if (ok == false) {
      return;
    }

    const updateDto = this.createUpdateDTO(true);
    if (updateDto == null) {
      return;
    }

    updateDto.batchUpdateTagCategories[0].op = "DELETE";
    await firstValueFrom(this.tagsApi.saveTags(updateDto));

    this.store.dispatch({ type: AppActions.loadTags.type });
  }

  //Tags
  showAddTagModal(category?: ReservationTagCategory) {
    this.selectedTag = new ReservationTag();
    this.selectedTag.categoryId = category != null ? category.id : this.categories[0].id + "";
    this.selectedTag.tagOrder = undefined;
    this.tagStartOrder = 0;
    this.tagModal.show(this.selectedTag);
  }

  editTag(cat: ReservationTagCategory, item: ReservationTag) {
    this.selectedCategory = cat;
    this.selectedTag = { ...item };
    this.tagStartOrder = item.tagOrder ?? 0;
    this.tagStartName = item.title;
    this.tagModal.show(item);
  }

  async tagModalClosed(tag: ReservationTag) {

  }

  showDeleteTagModal(cat: ReservationTagCategory, item: ReservationTag) {
    this.selectedCategory = new ReservationTagCategory(cat);
    this.selectedTag = { ...item };
    this.deleteTagModal.show();
  }

  async deleteTagModalClosed(ok: boolean) {
    if (ok == false) {
      return;
    }

    this.selectedCategory = this.categories.find(c => c.id == this.selectedTag.categoryId)!;
    const updateDto = this.createTagUpdateDTO(this.selectedTag, true);
    if (updateDto == null) {
      return;
    }
    updateDto.batchUpdateTagCategories[0].tags[0].op = "DELETE";
    await firstValueFrom(this.tagsApi.saveTags(updateDto));

    this.store.dispatch({ type: AppActions.loadTags.type });
  }

  private createUpdateDTO(forDeletion = false): BatchCategoryUpdate | null {
    const isNew = Common.isNullOrEmpty(this.selectedCategory.id);
    const reordered = isNew || this.selectedCategory.order != this.categoryStartOrder;
    const nameChanged = forDeletion || isNew || this.selectedCategory.name != this.categoryStartName;
    const colorChanged = forDeletion || isNew || this.selectedCategory.color != this.categoryStartColor;

    //nothing changed, early exit
    if (!reordered && !nameChanged && !colorChanged) {
      return null;
    }

    if (this.selectedCategory.order == null) {
      this.selectedCategory.order = this.categories.length + 1;
    }

    if (this.selectedCategory.order <= 1) {
      this.selectedCategory.order = 2;
    }

    let categoriesToUpdate: CategoryTagDTO[] = [
      {
        id: isNew ? "" : this.selectedCategory.id + "",
        restaurantId: this.localStorageService.getRestaurantId() + "",
        title: this.selectedCategory.name.trim(),
        color: this.selectedCategory.color,
        categoryOrder: this.selectedCategory.order ?? 1,
        categoryType: this.tagType,
        op: isNew ? "CREATE" : "EDIT",
        tags: []
      }
    ];

    if (forDeletion) {
      const sortedCategories = this.categories.map(c => c)
        .filter(c => c.id != this.selectedCategory.id && c.name != 'Dietary')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      let currentOrder = 2;
      for (let cat of sortedCategories) {
        if (currentOrder != cat.order) {
          cat.order = currentOrder;
          categoriesToUpdate.push(this.toRequestDTO(false, cat));
        }
        currentOrder++;
      }
    }

    if (!forDeletion && reordered) {
      const sortedCategories = this.categories.map(c => c)
        .filter(c => c.name != 'Dietary')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const lastOrder = (sortedCategories.length == 0 ? 2 : sortedCategories[sortedCategories.length - 1].order) ?? 1;
      if (this.selectedCategory.order > lastOrder) {
        this.selectedCategory.order = lastOrder + 1;
      }

      let positionToInsert = this.selectedCategory.order - 2;

      if (positionToInsert == -1) {
        positionToInsert = 0;
      }

      //move to the correrct array position
      if (isNew) {
        sortedCategories.splice(positionToInsert, 0, this.selectedCategory);
      } else {
        const existingPosition = sortedCategories.findIndex(c => c.id == this.selectedCategory.id);
        if (existingPosition > -1 && existingPosition != positionToInsert) {
          sortedCategories.splice(existingPosition, 1);
          sortedCategories.splice(positionToInsert, 0, this.selectedCategory);
        }
      }

      //change order based on array index
      let currentOrder = 2;
      for (let cat of sortedCategories) {
        if (currentOrder != cat.order) {
          cat.order = currentOrder;
          const existing = categoriesToUpdate.find(c => c.id == cat.id);
          if (existing == null) {
            categoriesToUpdate.push(this.toRequestDTO(false, cat));
          } else {
            existing.categoryOrder = currentOrder;
          }
        }
        currentOrder++;
      }

      /*if (this.selectedCategory.order > (sortedCategories[0]?.order ?? 0)) {
        this.selectedCategory.order = (sortedCategories[0]?.order ?? 0) + 1;
      }

      if (this.selectedCategory.order < 0) {
        this.selectedCategory.order = 0;
      }

      const nextCategories = this.categories
        .filter(c => (c.order ?? 0) >= (this.selectedCategory.order ?? 0) && (isNew || this.selectedCategory.id != c.id))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      let currenctOrder = this.selectedCategory.order ?? 0;
      const moveDown = this.selectedCategory.order > this.categoryStartOrder;

      if (moveDown && nextCategories.length > 0 && nextCategories[0]?.order == this.selectedCategory.order) {
        nextCategories[0].order--;
        categoriesToUpdate.push(this.toRequestDTO(false, nextCategories[0]));
        nextCategories.splice(0, 1);
      }

      for (let cat of nextCategories) {
        if (cat.name == 'Dietary') {
          continue;
        }

        currenctOrder++;
        if (cat.order != currenctOrder) {
          cat.order = currenctOrder;
          categoriesToUpdate.push(this.toRequestDTO(false, cat));
        }
      }*/
    }

    return {
      restaurantId: this.localStorageService.getRestaurantId() + "",
      batchUpdateTagCategories: categoriesToUpdate
    };
  }

  private toRequestDTO(isNew: boolean, cat: ReservationTagCategory): CategoryTagDTO {
    return {
      id: isNew ? "" : cat.id + "",
      restaurantId: this.localStorageService.getRestaurantId() + "",
      title: cat.name.trim(),
      color: cat.color,
      categoryOrder: cat.order ?? 1,
      categoryType: this.tagType,
      op: isNew ? "CREATE" : "EDIT",
      tags: []
    }
  }

  private createTagUpdateDTO(tag: ReservationTag, forDeletion = false): BatchCategoryUpdate | null {
    return createTagUpdateDTO(
      this.localStorageService.getRestaurantId() + "",
      this.selectedCategory, this.tagType, tag, this.tagStartOrder, this.tagStartName, forDeletion
    );
  }

}
